import { useEffect, useState } from "react";
import { doc, writeBatch, collection } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { useLang } from "../../lib/useLang";
import { isBackendConfigured } from "../../lib/catalog";
import { demoStore } from "../../lib/demoStore";
import { uploadDailyImage } from "../../lib/image";
import { scanMenuBoard } from "../../lib/menuScan";
import { dateKey } from "../../lib/format";
import type { DailyItem, Session } from "../../lib/types";

interface Row {
  id: string;
  name: string;
  nameAr: string;
  price: number;
  qty: number;
  active: boolean;
  fast: boolean;
  imageURL?: string;
}

function fromDaily(items: DailyItem[]): Row[] {
  return items.map((d) => ({
    id: d.id,
    name: d.name,
    nameAr: d.nameAr,
    price: d.price,
    qty: d.remainingQty,
    active: d.active,
    fast: d.fastDelivery ?? false,
    imageURL: d.imageURL,
  }));
}

/** Per-item photo picker: tap to choose (or shoot) a photo; shows a thumbnail. */
function PhotoUpload({
  id,
  imageURL,
  onChange,
}: {
  id: string;
  imageURL?: string;
  onChange: (url: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      onChange(await uploadDailyImage(id, file));
    } catch {
      /* ignore upload errors in the editor */
    } finally {
      setBusy(false);
    }
  }
  return (
    <label className="grid size-12 shrink-0 cursor-pointer place-items-center overflow-hidden rounded-lg bg-black/5 text-lg ring-1 ring-black/10 hover:ring-brand/40">
      {busy ? (
        <span className="text-xs text-black/40">…</span>
      ) : imageURL ? (
        <img src={imageURL} alt="" className="h-full w-full object-cover" />
      ) : (
        <span aria-hidden>📷</span>
      )}
      <input type="file" accept="image/*" className="hidden" onChange={onFile} />
    </label>
  );
}

export default function DailyMenuAdmin() {
  const { t } = useLang();
  const [rows, setRows] = useState<Row[]>([]);
  const [session, setSession] = useState<Session>("morning");
  const [status, setStatus] = useState("");

  // Seed the editor from the current daily menu (demo store or empty).
  useEffect(() => {
    setRows(fromDaily(demoStore.getDaily()));
  }, []);

  function update(id: string, patch: Partial<Row>) {
    setRows((r) => r.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  }

  function addSpecial() {
    const id = `daily-new-${rows.length}-${Math.floor(performance.now())}`;
    setRows((r) => [
      ...r,
      { id, name: "", nameAr: "", price: 0.5, qty: 10, active: true, fast: true },
    ]);
  }

  const [scanning, setScanning] = useState(false);
  const [scanNote, setScanNote] = useState("");

  async function onScan(file: File) {
    setScanning(true);
    setScanNote("");
    try {
      const { items, mode } = await scanMenuBoard(file);
      if (!items.length) {
        setScanNote(t("admin.scanNone"));
        return;
      }
      setRows((prev) => [
        ...prev,
        ...items.map((it, i) => ({
          id: `scan-${Math.floor(performance.now())}-${i}`,
          name: it.name,
          nameAr: it.nameAr,
          price: it.price,
          qty: it.qty,
          active: true,
          fast: true,
        })),
      ]);
      setScanNote(
        mode === "device"
          ? t("admin.scanDemo", { count: items.length })
          : t("admin.scanAdded", { count: items.length }),
      );
    } catch {
      setScanNote(t("admin.scanNone"));
    } finally {
      setScanning(false);
    }
  }

  function toDailyItems(batchId: string): DailyItem[] {
    return rows
      .filter((r) => r.name.trim())
      .map((r) => ({
        id: r.id,
        name: r.name.trim(),
        nameAr: r.nameAr.trim() || r.name.trim(),
        category: "daily",
        session,
        batchId,
        price: Number(r.price) || 0,
        initialQty: Number(r.qty) || 0,
        remainingQty: Number(r.qty) || 0,
        soldOut: (Number(r.qty) || 0) <= 0,
        active: r.active,
        fastDelivery: r.fast,
        imageURL: r.imageURL,
        updatedAt: Date.now(),
      }));
  }

  async function publish(batchLabel: "morning" | "evening") {
    const batchId = `${batchLabel}-${Date.now()}`;
    const items = toDailyItems(batchId);

    if (isBackendConfigured) {
      const date = dateKey();
      const batch = writeBatch(db);
      batch.set(doc(db, "dailyMenus", date), { status: "published" }, { merge: true });
      for (const it of items) {
        batch.set(doc(collection(db, "dailyMenus", date, "items"), it.id), it);
      }
      await batch.commit();
    } else {
      demoStore.setDaily(items);
    }

    setSession(batchLabel);
    setStatus(t("admin.published"));
    setTimeout(() => setStatus(""), 2500);
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-bold text-gray-900">{t("admin.daily")}</h2>
          <p className="text-xs text-black/50">{dateKey()} · {t(`menu.session.${session}`)}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="btn-accent cursor-pointer text-sm">
            📷 {scanning ? t("admin.scanning") : t("admin.scan")}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              disabled={scanning}
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) onScan(f);
              }}
            />
          </label>
          <button className="btn-outline text-sm" onClick={addSpecial}>
            + {t("admin.addSpecial")}
          </button>
        </div>
      </div>
      {scanNote && (
        <p className="rounded-xl bg-cream p-2.5 text-xs text-black/60">{scanNote}</p>
      )}

      {/* Editable rows */}
      <div className="space-y-2">
        <div className="hidden grid-cols-[1fr_5rem_4rem_3rem] gap-2 px-1 text-xs font-semibold text-black/45 sm:grid">
          <span>{t("admin.itemName")}</span>
          <span>{t("admin.price")}</span>
          <span>{t("admin.qty")}</span>
          <span>{t("admin.active")}</span>
        </div>
        {rows.map((r) => (
          <div
            key={r.id}
            className="grid grid-cols-2 items-center gap-2 rounded-xl bg-white p-2 ring-1 ring-black/5 sm:grid-cols-[1fr_5rem_4rem_3rem]"
          >
            <div className="col-span-2 flex items-start gap-2 sm:col-span-1">
              <PhotoUpload
                id={r.id}
                imageURL={r.imageURL}
                onChange={(url) => update(r.id, { imageURL: url })}
              />
              <div className="grid flex-1 gap-1">
                <input
                  className="input py-1.5"
                  placeholder={t("admin.itemName")}
                  value={r.name}
                  onChange={(e) => update(r.id, { name: e.target.value })}
                />
                <input
                  className="input py-1.5 text-sm"
                  placeholder={t("admin.itemNameAr")}
                  dir="rtl"
                  value={r.nameAr}
                  onChange={(e) => update(r.id, { nameAr: e.target.value })}
                />
                <button
                  type="button"
                  onClick={() => update(r.id, { fast: !r.fast })}
                  className={`chip w-fit ${
                    r.fast ? "bg-brand/10 text-brand" : "bg-black/5 text-black/40"
                  }`}
                >
                  ⚡ {t("common.fast")}
                </button>
              </div>
            </div>
            <input
              className="input py-1.5"
              type="number"
              step="0.05"
              min="0"
              value={r.price}
              onChange={(e) => update(r.id, { price: Number(e.target.value) })}
            />
            <input
              className="input py-1.5"
              type="number"
              min="0"
              value={r.qty}
              onChange={(e) => update(r.id, { qty: Number(e.target.value) })}
            />
            <label className="flex items-center gap-2 text-sm font-medium text-black/55 sm:justify-center sm:text-transparent">
              <input
                type="checkbox"
                className="h-6 w-6 accent-brand"
                checked={r.active}
                onChange={(e) => update(r.id, { active: e.target.checked })}
              />
              <span className="sm:hidden">{t("admin.active")}</span>
            </label>
          </div>
        ))}
      </div>

      {/* Publish controls */}
      <div className="flex flex-wrap items-center gap-2">
        <button className="btn-primary" onClick={() => publish("morning")}>
          {t("admin.publishMorning")}
        </button>
        <button className="btn-accent" onClick={() => publish("evening")}>
          {t("admin.startEvening")}
        </button>
        {status && (
          <span className="chip bg-accent/10 font-semibold text-accent-dark">✔ {status}</span>
        )}
      </div>
    </div>
  );
}
